import { type GraphQLTaggedNode } from 'react-relay';
import { type IEnvironment, type ReaderFragment } from 'relay-runtime';

import { addTypenameToData, writeFragmentShim } from '../fragmentCacheOperations.ts';
import { toDocumentNode } from '../toDocumentNode.ts';

import {
	type CacheBridgeCache,
	type CacheWriteResult,
	getFailureContext,
	getSecondaryCache,
	skipped,
	writeSafely,
	written,
} from './cacheBridgeCommon.ts';
import { getApolloRelayCacheBridgeDisabledReason } from './cacheBridgeGate.ts';

export type FragmentCacheBridgeSource = CacheBridgeCache;

type ApolloFragmentClient = {
	readFragment<TData>(options: {
		id: string;
		fragment: unknown;
		variables?: Record<string, any>;
	}): TData | null;
	writeFragment<TData>(options: {
		id: string;
		fragment: unknown;
		data: TData;
		variables?: Record<string, any>;
	}): void;
};

export type FragmentCacheBridgeReadArgs = {
	fragment: GraphQLTaggedNode;
	fragmentId?: string;
	variables?: Record<string, any>;
	useAggClient?: boolean;
	apolloClient?: ApolloFragmentClient;
	relayEnvironment?: IEnvironment;
};

export type FragmentCacheBridgeWriteArgs<TData> = FragmentCacheBridgeReadArgs & {
	fragmentId: string;
	data?: TData | null;
	fragmentArtifact?: ReaderFragment;
};

export type FragmentCacheBridgeSyncArgs<TData> = FragmentCacheBridgeWriteArgs<TData> & {
	source: FragmentCacheBridgeSource;
};

function shouldSkipSyncCache<TData>({ data }: FragmentCacheBridgeWriteArgs<TData>): string | null {
	if (data == null) return 'data-missing';
	if (typeof window === 'undefined') return 'window-missing';

	return null;
}

function getApolloClient(
	useAggClient?: boolean,
	apolloClient?: ApolloFragmentClient,
): ApolloFragmentClient | undefined {
	if (typeof window === 'undefined') return apolloClient;

	if (useAggClient) {
		return (window['GLOBAL_APOLLO_AGG_CLIENT'] ?? (window as any)['MOCK_GATEWAY_APOLLO_CLIENT']) as
			| ApolloFragmentClient
			| undefined;
	}

	return apolloClient ?? (window['GLOBAL_APOLLO_CLIENT'] as ApolloFragmentClient | undefined);
}

function getRelayEnvironment(relayEnvironment?: IEnvironment) {
	if (relayEnvironment) return relayEnvironment;
	if (typeof window === 'undefined') return undefined;

	return window['GLOBAL_RELAY_ENV'];
}

function writeFragmentToApollo<TData>(args: FragmentCacheBridgeWriteArgs<TData>): CacheWriteResult {
	const skipReason = shouldSkipSyncCache(args);
	if (skipReason) return skipped(skipReason);

	const client = getApolloClient(args.useAggClient, args.apolloClient);
	if (!client) return skipped('apollo-client-missing');

	const dataToWrite = args.fragmentArtifact
		? (addTypenameToData(
				args.data as Record<string, any>,
				args.fragmentArtifact,
			) as unknown as TData)
		: args.data;

	return writeSafely(
		() => {
			client.writeFragment<TData>({
				id: args.fragmentId,
				fragment: toDocumentNode(args.fragment),
				data: dataToWrite as TData,
				variables: args.variables,
			});
			return written();
		},
		getFailureContext('fragment', 'write', 'apollo'),
	);
}

function writeFragmentToRelay<TData>(args: FragmentCacheBridgeWriteArgs<TData>): CacheWriteResult {
	const skipReason = shouldSkipSyncCache(args);
	if (skipReason) return skipped(skipReason);

	const environment = getRelayEnvironment(args.relayEnvironment);
	if (!environment) return skipped('relay-environment-missing');

	return writeSafely(
		() => {
			const didWrite = writeFragmentShim(
				environment,
				args.fragment,
				args.fragmentId,
				args.data as TData,
				args.variables,
			);

			return didWrite ? written() : skipped('no-write-performed');
		},
		getFailureContext('fragment', 'write', 'relay'),
	);
}

function writeFragmentToCache<TData>(
	args: FragmentCacheBridgeWriteArgs<TData>,
	targetCache: CacheBridgeCache,
): CacheWriteResult {
	return targetCache === 'apollo' ? writeFragmentToApollo(args) : writeFragmentToRelay(args);
}

export function syncFragmentCacheBridge<TData>({
	source,
	...writeArgs
}: FragmentCacheBridgeSyncArgs<TData>): CacheWriteResult {
	const disabledReason = getApolloRelayCacheBridgeDisabledReason();
	if (disabledReason) return skipped(disabledReason);

	return writeFragmentToCache(writeArgs, getSecondaryCache(source));
}
